<template>
    <div class="tab-content p-0 bg-light1" id="nav-tabContent">
        <div class="tab-pane fade active show" id="nav-home" role="tabpanel" aria-labelledby="nav-home-tab">

            <div v-if="status" class="table-responsive table-responsive-sm">
                <table class="table table-striped table-sm text-nowrap1 table-bordered mb-0">
                    <tbody>
                        <tr>
                            <th>Receipt No</th>
                            <td>{{ basicDetail.receipt_no }}</td>
                        </tr>
                        <tr>
                            <th>Supplier</th>
                            <td>{{ basicDetail.supplier?.supplier_name }}</td>
                        </tr>
                        <tr>
                            <th>Plant</th>
                            <td>{{ basicDetail.plant?.plant_name }}</td>
                        </tr>
                        <tr>
                            <th>Wagon No.</th>
                            <td>{{ basicDetail?.wagon_no }}</td>
                        </tr>
                    </tbody>
                </table>

                <div class="mt-3">
                    <h6 class="mb-2">Inspection Lots</h6>
                    <div v-if="!inspectionLots.length" class="text-muted py-2">
                        No lots found for this inspection.
                    </div>
                    <div
                        v-for="(lot, lotIndex) in inspectionLots"
                        :key="lot.inspection_lot_id || lot.lot_no || lotIndex"
                        class="card mb-2 border"
                    >
                        <div class="card-body py-2">
                            <div class="row align-items-center g-2">
                                <div class="col-md-8">
                                    <div class="fw-semibold">Lot {{ lot.lot_no }}</div>
                                    <div class="small text-muted">
                                        Pallet size:
                                        {{ lot.pallet_size?.pallet_size_name || '—' }}                                     
                                        <span v-if="lot.weight"> · Weight: {{ lot.weight }}</span>
                                    </div>
                                </div>
                                <div class="col-md-4 d-flex flex-wrap gap-2 justify-content-md-end">
                                    <button
                                        type="button"
                                        class="btn btn-sm btn-outline-primary"
                                        @click="openLotPalletsModal(lot)"
                                    >
                                        <i class="ri-stack-line"></i> View pallets
                                        ({{ lotPalletCount(lot) }})
                                    </button>
                                    <button
                                        type="button"
                                        class="btn btn-sm btn-outline-secondary"
                                        @click="openLotAttachmentsModal(lot)"
                                    >
                                        <i class="ri-image-line"></i> View attachments
                                        ({{ lotAttachmentCount(lot) }})
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <br />
            </div>
            <div v-else class="">
                <table class="table table-striped table-sm text-nowrap1 table-bordered mb-0">
                    <thead>
                        <tr class="bg-light1" style="background-color: #9b9b9b; color: white;">
                            <th>Check Name</th>
                            <th>Value</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="value, key in item" :key="key">
                            <td>{{ value.check_name }}</td>
                            <td>{{ value.default_value }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>

    <div class="modal fade" tabindex="-1" id="viewLotPalletsModal" ref="viewLotPalletsModal">
        <div class="modal-dialog modal-lg modal-dialog-scrollable">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">
                        Pallets — Lot {{ activeViewLot?.lot_no || '—' }}
                    </h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                    <div v-if="!lotPalletsModalList.length" class="text-muted text-center py-3">
                        No pallet details for this lot.
                    </div>
                    <div v-else class="table-responsive">
                        <table class="table table-sm table-bordered mb-0">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Moisture</th>
                                    <th>Remarks</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="(pallet, index) in lotPalletsModalList"
                                    :key="pallet.inspection_lot_pallet_id || index"
                                >
                                    <td>{{ index + 1 }}</td>
                                    <td>{{ pallet.moisture ?? '—' }}</td>
                                    <td>{{ pallet.remarks || '—' }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                </div>
            </div>
        </div>
    </div>

    <div class="modal fade" tabindex="-1" id="viewLotAttachmentsModal" ref="viewLotAttachmentsModal">
        <div class="modal-dialog modal-lg modal-dialog-scrollable">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">
                        Attachments — Lot {{ activeViewLot?.lot_no || '—' }}
                    </h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                    <div v-if="!lotAttachmentsModalList.length" class="text-muted text-center py-3">
                        No images for this lot.
                    </div>
                    <div v-else class="row g-2">
                        <div
                            v-for="(attachment, imgIndex) in lotAttachmentsModalList"
                            :key="attachment.inspection_lot_attachment_id || imgIndex"
                            class="col-md-4 col-sm-6"
                        >
                            <div class="card h-100">
                                <div class="card-body text-center p-2">
                                    <img
                                        v-if="getLotAttachmentSrc(attachment)"
                                        class="img-fluid rounded mb-2"
                                        style="max-height: 120px; object-fit: cover"
                                        :src="getLotAttachmentSrc(attachment)"
                                        alt="Lot attachment"
                                    />
                                    <div
                                        v-else
                                        class="text-muted small py-4"
                                    >
                                        Preview unavailable
                                    </div>
                                    <div
                                        class="small text-muted text-truncate"
                                        :title="attachment.attachment_name"
                                    >
                                        {{ attachment.attachment_name || 'Image' }}
                                    </div>
                                </div>
                                <div class="card-footer d-flex justify-content-center p-2">
                                    <a
                                        v-if="attachment.attachment_name"
                                        :href="$store.getters.apiUrl + 'downloadImage?filename=' + attachment.attachment_name"
                                        target="_blank"
                                        class="btn btn-sm btn-primary"
                                    >
                                        <i class="ri-download-line"></i> Download
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "InspectionChecks",
    props: {
        item: Array,
        basicDetail: Object,
        status: Boolean,
        inspection_attachments: Array,
    },
    data() {
        return {
            inspection_check_attachments: [],
            check_name: "",
            inspectionCheck: {},
            checkAttachmentStatus: false,
            activeViewLot: null,
            lotPalletsModalList: [],
            lotAttachmentsModalList: [],
            lotPalletsModalInstance: null,
            lotAttachmentsModalInstance: null,
        };
    },

    computed: {
        inspectionLots() {
            return this.basicDetail?.inspection_lots || [];
        },
    },

    mounted() {
        this.inspection_check_attachments = [];
    },

    methods: {
        lotPalletCount(lot) {
            return (lot?.inspection_lot_pallets || []).length;
        },

        lotAttachmentCount(lot) {
            return (lot?.inspection_lot_attachments || []).length;
        },

        openLotPalletsModal(lot) {
            this.activeViewLot = lot;
            const existing = lot?.inspection_lot_pallets || [];
            if (existing.length || !lot?.inspection_lot_id) {
                this.lotPalletsModalList = [...existing];
                this.showModal("viewLotPalletsModal", "lotPalletsModalInstance");
                return;
            }
            const loader = this.$loading.show();
            this.$store
                .dispatch("post", {
                    uri: "getInspectionLotPallets",
                    data: { inspection_lot_id: lot.inspection_lot_id },
                })
                .then((response) => {
                    loader.hide();
                    const list = response.data?.data || response.data || [];
                    this.lotPalletsModalList = Array.isArray(list) ? list : [];
                    this.showModal("viewLotPalletsModal", "lotPalletsModalInstance");
                })
                .catch((error) => {
                    loader.hide();
                    this.$store.dispatch(
                        "error",
                        error.response?.data?.message || "Failed to load pallets"
                    );
                });
        },

        openLotAttachmentsModal(lot) {
            this.activeViewLot = lot;
            const existing = lot?.inspection_lot_attachments || [];
            if (existing.length || !lot?.inspection_lot_id) {
                this.lotAttachmentsModalList = [...existing];
                this.showModal("viewLotAttachmentsModal", "lotAttachmentsModalInstance");
                return;
            }
            const loader = this.$loading.show();
            this.$store
                .dispatch("post", {
                    uri: "getInspectionLotAttachments",
                    data: { inspection_lot_id: lot.inspection_lot_id },
                })
                .then((response) => {
                    loader.hide();
                    const list = response.data?.data || response.data || [];
                    this.lotAttachmentsModalList = Array.isArray(list) ? list : [];
                    this.showModal("viewLotAttachmentsModal", "lotAttachmentsModalInstance");
                })
                .catch((error) => {
                    loader.hide();
                    this.$store.dispatch(
                        "error",
                        error.response?.data?.message || "Failed to load attachments"
                    );
                });
        },

        showModal(refName, instanceKey) {
            const el = this.$refs[refName];
            if (window.bootstrap?.Modal && el) {
                this[instanceKey] = window.bootstrap.Modal.getOrCreateInstance(el);
                this[instanceKey].show();
            }
        },

        getLotAttachmentSrc(attachment) {
            if (attachment?.attachment_path) {
                return attachment.attachment_path;
            }
            if (attachment?.attachment_name) {
                return (
                    this.$store.getters.apiUrl +
                    "downloadImage?filename=" +
                    attachment.attachment_name
                );
            }
            return "";
        },

        show(inspection_check) {
            this.checkAttachmentStatus = true;
            this.inspectionCheck = inspection_check;
            this.inspection_check_attachments = inspection_check.inspection_attachments;
            this.check_name = inspection_check.check_name;
        },
    },
};
</script>
